import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useEffect, useState } from "react";

import {
  BackButton,
  Button,
  Checkbox,
  LoadingCircle,
  useToast,
  TextButton,
  ConfirmationDialog,
  Tag,
  TagVariant,
} from "@industriousoffice/react-components";
import useAccount from "@/hooks/useAccount";
import { InvoiceSummary } from "@/pages/PayInvoices/components/InvoiceSummary";
import { InvoiceCard } from "@/pages/PayInvoices/components/InvoiceCard";
import PaymentMethodTile from "@/components/PaymentMethodTile";
import {
  ZuoraPaymentMethodTypes,
  Invoice,
  AccountSignature,
  PaymentErrorMessages,
  InvoicingSystemTypes,
  StripePaymentMethodTypes,
} from "@/api/opb/opbTypes";
import { SplittedPage } from "@/components/SplittedPage/SplittedPage";
import { CREDIT_CARD_FEE_THRESHOLD } from "@/constants";
import { ACHInfo } from "@/pages/PaymentMethods/components/ACHInfo";
import { CreditCardInfo } from "@/pages/PaymentMethods/components/CreditCardInfo";
import { formatCurrency } from "@/utils/string";
import { PaymentConfirmation } from "@/pages/PayInvoices/components/PaymentConfirmation";
import { opbApi } from "@/api/opb/opbApi";
import { ApiError } from "@/api/types";
import { PageTitle } from "@/components/PageTitle";
import { Add3dsCreditCardDrawer } from "@/pages/PaymentMethods/components/Add3dsCreditCardDrawer";
import { AddAchAccountDrawer } from "@/pages/PaymentMethods/components/AddAchAccountDrawer";
import { AddBacsAccountDrawer } from "@/pages/PaymentMethods/components/AddBacsAccountDrawer";
import { AddPadAccountDrawer } from "@/pages/PaymentMethods/components/AddPadAccountDrawer";
import { AddPaymentMethodDrawer } from "@/pages/PaymentMethods/components/AddPaymentMethodDrawer";
import { AddSepaAccountDrawer } from "@/pages/PaymentMethods/components/AddSepaAccountDrawer";
import { PadInfo } from "@/pages/PaymentMethods/components/PadInfo";
import { BacsInfo } from "@/pages/PaymentMethods/components/BacsInfo";
import { SepaInfo } from "@/pages/PaymentMethods/components/SepaInfo";
import { slugs } from "@/router/slugs";
import { getFullPath } from "@/router/utils/getFullPath";
import {
  usesACH,
  usesBacs,
  usesPad,
  usesSepa,
  uses3dsCreditCard,
} from "@/utils/location";
import { formatPaymentError } from "@/utils/payment-errors";
import { bankTransferType } from "../PaymentMethods/utils/bankTransferType";
import { StripeACHInfo } from "../PaymentMethods/components/StripeACHInfo";
import { StripeCreditCardInfo } from "../PaymentMethods/components/StripeCreditCardInfo";
import { StripePadInfo } from "../PaymentMethods/components/StripePadInfo";
import { StripeBacsInfo } from "../PaymentMethods/components/StripeBacsInfo";
import { StripeSepaInfo } from "../PaymentMethods/components/StripeSepaInfo";

const MAX_INVOICES_TO_SEND_ADD_PAYMENT_CALLBACK = 20;

export function PayInvoices() {
  const { accountId } = useParams();
  const { state } = useLocation();
  const { initialInvoiceIds, initialPaymentMethodId, hasError } = state ?? {};
  const navigate = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation("payInvoices");

  const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >();
  const [isPaymentSuccess, setPaymentSuccess] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [
    isCreditCardFeePaymentConfirmationOpen,
    setIsCreditCardFeePaymentConfirmationOpen,
  ] = useState(false);
  const [isOpenAddPaymentMethodDrawer, setIsOpenAddPaymentMethodDrawer] =
    useState<boolean>(false);
  const [isOpenAdd3dsCreditCardDrawer, setIsOpenAdd3dsCreditCardDrawer] =
    useState<boolean>(false);
  const [isOpenAddAchAccountDrawer, setIsOpenAddAchAccountDrawer] =
    useState<boolean>(false);
  const [isOpenAddBacsAccountDrawer, setIsOpenAddBacsAccountDrawer] =
    useState<boolean>(false);
  const [isOpenAddPadAccountDrawer, setIsOpenAddPadAccountDrawer] =
    useState<boolean>(false);
  const [isOpenAddSepaAccountDrawer, setIsOpenAddSepaAccountDrawer] =
    useState<boolean>(false);
  const [creditCardSignature, setCreditCardSignature] =
    useState<AccountSignature>();
  const [threeDSCreditCardSignature, setThreeDSCreditCardSignature] =
    useState<AccountSignature>();
  const [achSignature, setAchSignature] = useState<AccountSignature>();
  const [bacsSignature, setBacsSignature] = useState<AccountSignature>();
  const [padSignature, setPadSignature] = useState<AccountSignature>();
  const [sepaSignature, setSepaSignature] = useState<AccountSignature>();

  const {
    name,
    invoices,
    account,
    isLoading,
    zuoraPaymentMethods,
    stripePaymentMethods,
    location,
  } = useAccount(accountId, {
    withInvoices: true,
    withBalance: true,
    withPaymentMethods: true,
  });

  const isStripeBilling =
    account?.invoicingSystemType === InvoicingSystemTypes.StripeBilling;

  const formattedThresholdAmount = formatCurrency({
    value: CREDIT_CARD_FEE_THRESHOLD,
    currency: location?.currencyCode || "USD",
    lang: language,
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });

  const { component: toastComponent, toast } = useToast();

  useEffect(() => {
    if (!invoices.length) return;
    if (initialInvoiceIds?.length) {
      const invoicesToSelect = invoices.filter((i) =>
        initialInvoiceIds.includes(i.id)
      );
      setSelectedInvoices(
        invoicesToSelect.length ? invoicesToSelect : invoices
      );
      return;
    }
    setSelectedInvoices(invoices);
  }, [invoices.length, initialInvoiceIds]);

  useEffect(() => {
    if (hasError) toast(t("Sorry, something went wrong."), true);
    navigate(getFullPath(slugs.payInvoices, { accountId }), {
      replace: true,
      state: { ...state, hasError: undefined }, // clear the error from state
    });
  }, [hasError]);

  useEffect(() => {
    if (!zuoraPaymentMethods.length) return;
    if (initialPaymentMethodId) {
      const paymentMethodToSelect = isStripeBilling
        ? stripePaymentMethods.find((pm) => pm.id === initialPaymentMethodId)
        : zuoraPaymentMethods.find((pm) => pm.id === initialPaymentMethodId);
      if (paymentMethodToSelect)
        setSelectedPaymentMethodId(paymentMethodToSelect.id);
    }
  }, [zuoraPaymentMethods.length, initialPaymentMethodId]);

  useEffect(() => {
    if (account?.defaultPaymentMethodId) {
      setSelectedPaymentMethodId(account.defaultPaymentMethodId);
    }
  }, [account?.defaultPaymentMethodId]);

  // scroll to the top before showing payment confirmation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isPaymentSuccess]);

  useEffect(() => {
    if (isStripeBilling || !account) return;

    if (accountId) {
      Promise.all([
        opbApi.createAccountSignature(accountId, "3ds-credit-card"),
        opbApi.createAccountSignature(accountId, "credit-card"),
        opbApi.createAccountSignature(accountId, "ach"),
        opbApi.createAccountSignature(accountId, "pad"),
        opbApi.createAccountSignature(accountId, "bacs"),
        opbApi.createAccountSignature(accountId, "sepa"),
      ])
        .then(
          ([
            currentAccount3dsCreditCardSignature,
            currentAccountCreditCardSignature,
            currentAccountAchSignature,
            currentPadAccountSignature,
            currentBacsAccountSignature,
            currentSepaAccountSignature,
          ]) => {
            setCreditCardSignature(currentAccountCreditCardSignature);
            setAchSignature(currentAccountAchSignature);
            setPadSignature(currentPadAccountSignature);
            setBacsSignature(currentBacsAccountSignature);
            setSepaSignature(currentSepaAccountSignature);
            setThreeDSCreditCardSignature(currentAccount3dsCreditCardSignature);
          }
        )
        .catch(() => toast(t("Sorry, something went wrong."), true));
    }
  }, [account]);

  const onToggleInvoice = (invoice: Invoice) => {
    setSelectedInvoices(
      selectedInvoices.map((i) => i.id).includes(invoice.id)
        ? selectedInvoices.filter((i) => i.id !== invoice.id)
        : [...selectedInvoices, invoice]
    );
  };

  const submitPayment = async () => {
    if (!accountId || !selectedInvoices.length || !selectedPaymentMethodId)
      return;
    try {
      setIsPaying(true);
      await opbApi.payInvoices({
        accountId,
        invoiceIds: selectedInvoices.map((i) => i.id),
        paymentMethodId: selectedPaymentMethodId,
        isStripeBilling,
      });
      setPaymentSuccess(true);
    } catch (error) {
      const maybeAxiosError = error as ApiError;
      const selectedPaymentMethod = isStripeBilling
        ? stripePaymentMethods?.find?.((p) => p.id === selectedPaymentMethodId)
        : zuoraPaymentMethods?.find?.((p) => p.id === selectedPaymentMethodId);
      if (
        selectedPaymentMethod?.type === ZuoraPaymentMethodTypes.Pad &&
        maybeAxiosError?.response?.status === 400 &&
        // for now the only way to detect zuora-specific errors is to check the error message:
        maybeAxiosError?.response?.data?.error?.message?.indexOf?.(
          PaymentErrorMessages.NotValidated
        ) > -1
      ) {
        // when a PAD account is not validated, Zuora returns a 400 error with a rather cryptic message:
        // "There was an error processing your payment: [invalid_request_error] The provided PaymentMethod cannot be attached. To reuse a PaymentMethod, you must attach it to a Customer first."
        toast(t("This account has not been validated."), true);
      } else {
        const errorMessage =
          maybeAxiosError?.response?.data?.error?.message || "Unknown error";
        const formattedError = formatPaymentError(errorMessage);
        const formattedErrorMsg = `${t(formattedError.message)}${
          formattedError.code ? formattedError.code : ""
        }`;
        toast(formattedErrorMsg, true);
      }
    } finally {
      setIsPaying(false);
    }
  };

  const selectedPaymentMethod = isStripeBilling
    ? stripePaymentMethods?.find?.((p) => p.id === selectedPaymentMethodId)
    : zuoraPaymentMethods?.find?.((p) => p.id === selectedPaymentMethodId);

  const formatAmount = (amount: number) => {
    if (!account?.currencyCode) return "";
    return formatCurrency({
      value: amount,
      currency: account?.currencyCode,
      lang: language,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const balance = selectedInvoices.reduce((acc, i) => acc + i.balance, 0);
  const balanceToPay = formatAmount(balance);

  const creditCardFeeRate = location?.creditCardFeeRate || 0;
  const creditCardFeeRequired =
    balance >= CREDIT_CARD_FEE_THRESHOLD &&
    creditCardFeeRate > 0 &&
    selectedPaymentMethod?.type === ZuoraPaymentMethodTypes.CreditCard;
  const creditCardFee = creditCardFeeRequired
    ? balance * (creditCardFeeRate / 100)
    : 0;
  const creditCardFeeToPay = formatAmount(creditCardFee);

  const totalPayment = balance + creditCardFee;
  const totalPaymentToPay = formatAmount(totalPayment);

  if (isPaymentSuccess && account?.currencyCode && accountId) {
    if (selectedPaymentMethod) {
      return (
        <PaymentConfirmation
          invoices={selectedInvoices}
          paymentMethod={selectedPaymentMethod}
          currencyCode={account?.currencyCode}
          locationName={name}
          creditCardFeePaid={creditCardFeeRequired ? creditCardFeeToPay : ""}
          totalPaymentPaid={totalPaymentToPay}
          accountId={accountId}
        />
      );
    }
  }

  const onSubmitPayment = async () => {
    if (creditCardFeeRequired) {
      setIsCreditCardFeePaymentConfirmationOpen(true);
    } else {
      submitPayment();
    }
  };

  const onToggleAllInvoices = () => {
    if (selectedInvoices.length) {
      setSelectedInvoices([]);
      return;
    }
    setSelectedInvoices(invoices);
  };

  const onCancelCreditCardFeeDialog = () => {
    setIsCreditCardFeePaymentConfirmationOpen(false);
  };

  const onCloseConfirmation = () => {
    setIsCreditCardFeePaymentConfirmationOpen(false);
  };

  const shouldShowACH = usesACH(location);
  const shouldShowPAD = usesPad(location);
  const shouldShowBacs = usesBacs(location);
  const shouldShowSepa = usesSepa(location);
  const shouldShow3dsCreditCard = uses3dsCreditCard(location);
  const pathname = getFullPath(slugs.payInvoices, { accountId });

  const navigateToPaymentsMethod = () => {
    navigate(getFullPath(slugs.paymentMethods, { accountId }));
  };

  const PaymentSummary = (
    <div className="m-5 grow">
      <div className="flex flex-row justify-between font-bold mb-3.5">
        <span>{t("Invoice Total")}: </span>
        <span>{balanceToPay}</span>
      </div>
      <div className="flex flex-row justify-between font-bold">
        <span>{t("Card Processing Fee")}: </span>
        <span>{creditCardFeeToPay}</span>
      </div>
      <Tag
        text="Automatically Processed"
        variant={TagVariant.Info}
        className="mb-5 rounded-xs"
      />
      <div className="flex flex-row justify-between pt-4 mb-6 font-bold border-t-2 border-solid border-grey-60">
        <span>{t("Total Payment")}: </span>
        <span>{totalPaymentToPay}</span>
      </div>
      <div className="flex flex-row justify-between font-bold mb-3.5">
        {selectedPaymentMethod?.type === ZuoraPaymentMethodTypes.CreditCard
          ? `${selectedPaymentMethod.cardType.toUpperCase()} ${selectedPaymentMethod.cardNumber.slice(
              -8
            )}`
          : ""}
      </div>
      <TextButton
        className="text-base! font-bold hover:bg-teal-99 rounded-full px-2"
        onClick={navigateToPaymentsMethod}
        data-testid="dashboard-manage_payment_methods-btn"
      >
        {t("Manage Payment Method")}
      </TextButton>
    </div>
  );

  return (
    <>
      {toastComponent}
      <SplittedPage>
        <SplittedPage.Left>
          <div className="mb-4">
            <BackButton onClick={() => navigate(-1)}>{t("Back")}</BackButton>
          </div>
          <PageTitle data-testid="pay_invoices-title">
            {t("Pay Invoices at", { name })}
          </PageTitle>
          <div className="pt-2 text-grey-30">
            {t("Select the invoices you want to pay.")}
          </div>
          <div className="mt-8 sm:rounded-sm sm:border sm:border-grey-60">
            <div className="flex items-center pb-3 text-sm sm:border-b sm:border-grey-60 sm:pl-6 sm:bg-almond-99 sm:py-1 sm:text-base">
              <Checkbox
                onChange={onToggleAllInvoices}
                checked={selectedInvoices.length === invoices.length}
                isPartial={
                  selectedInvoices.length !== invoices.length &&
                  Boolean(selectedInvoices.length)
                }
                data-testid="pay_invoices_select_all_invoices-checkbox"
                className="mr-5 sm:mr-6"
              />
              <span className="font-medium">{t("Invoices Selected")}</span>
              <span className="ml-4 font-light">
                {t("count of total selected", {
                  count: selectedInvoices.length,
                  total: invoices.length,
                })}
              </span>
            </div>
            {!isLoading && account?.currencyCode && accountId ? (
              invoices.map((invoice, index) => (
                <div className="mb-4 sm:mb-0" key={invoice.id}>
                  <InvoiceCard
                    invoice={invoice}
                    onToggleInvoice={onToggleInvoice}
                    isInvoiceSelected={selectedInvoices
                      .map((i) => i.id)
                      .includes(invoice.id)}
                    name={name}
                    currencyCode={account.currencyCode}
                    withCheckbox
                    accountId={accountId}
                    isLast={index === invoices.length - 1}
                  />
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center py-7">
                <LoadingCircle spinnerColor="#013e3f" />
              </div>
            )}
          </div>
          <div className="mt-8 text-lg font-bold">{t("Payment Method")}</div>
          <div className="grid grid-cols-1 gap-6 mt-8 mb-6 md:grid-cols-2 2xl:grid-cols-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-7">
                <LoadingCircle spinnerColor="#013e3f" />
              </div>
            ) : (
              <>
                {zuoraPaymentMethods.map((paymentMethod) => (
                  <PaymentMethodTile
                    key={paymentMethod.id}
                    className={`flex py-6 cursor-default border-2 ${
                      selectedPaymentMethodId === paymentMethod.id
                        ? "border-teal-60"
                        : "border-grey-60"
                    }`}
                    onChange={() => {
                      setSelectedPaymentMethodId(paymentMethod.id);
                    }}
                  >
                    {(() => {
                      switch (paymentMethod.type) {
                        case ZuoraPaymentMethodTypes.ACH:
                          return (
                            <ACHInfo
                              paymentMethod={paymentMethod}
                              footer={
                                <div className="mt-2 text-sm text-right text-ocean-60">
                                  {t(
                                    selectedPaymentMethodId === paymentMethod.id
                                      ? "Selected"
                                      : "Use this account"
                                  )}
                                </div>
                              }
                            />
                          );
                        case ZuoraPaymentMethodTypes.CreditCard:
                          return (
                            <CreditCardInfo
                              paymentMethod={paymentMethod}
                              footer={
                                <div className="mt-2 text-sm text-right text-ocean-60">
                                  {t(
                                    selectedPaymentMethodId === paymentMethod.id
                                      ? "Selected"
                                      : "Use this card"
                                  )}
                                </div>
                              }
                            />
                          );
                        case ZuoraPaymentMethodTypes.Pad:
                          return (
                            <PadInfo
                              paymentMethod={paymentMethod}
                              footer={
                                <div className="mt-2 text-sm text-right text-ocean-60">
                                  {t(
                                    selectedPaymentMethodId === paymentMethod.id
                                      ? "Selected"
                                      : "Use this account"
                                  )}
                                </div>
                              }
                            />
                          );
                        case ZuoraPaymentMethodTypes.Bacs:
                          return (
                            <BacsInfo
                              paymentMethod={paymentMethod}
                              footer={
                                <div className="mt-2 text-sm text-right text-ocean-60">
                                  {t(
                                    selectedPaymentMethodId === paymentMethod.id
                                      ? "Selected"
                                      : "Use this account"
                                  )}
                                </div>
                              }
                            />
                          );
                        case ZuoraPaymentMethodTypes.Sepa:
                          return (
                            <SepaInfo
                              paymentMethod={paymentMethod}
                              footer={
                                <div className="mt-2 text-sm text-right text-ocean-60">
                                  {t(
                                    selectedPaymentMethodId === paymentMethod.id
                                      ? "Selected"
                                      : "Use this account"
                                  )}
                                </div>
                              }
                            />
                          );
                        default:
                          return null;
                      }
                    })()}
                  </PaymentMethodTile>
                ))}
                {stripePaymentMethods.map((paymentMethod) => (
                  <PaymentMethodTile
                    key={paymentMethod.id}
                    className={`flex py-6 cursor-default border-2 ${
                      selectedPaymentMethodId === paymentMethod.id
                        ? "border-teal-60"
                        : "border-grey-60"
                    }`}
                    onChange={() => {
                      setSelectedPaymentMethodId(paymentMethod.id);
                    }}
                  >
                    {(() => {
                      switch (paymentMethod.type) {
                        case StripePaymentMethodTypes.ACH:
                          return (
                            <StripeACHInfo
                              paymentMethod={paymentMethod}
                              withEdit
                            />
                          );
                        case StripePaymentMethodTypes.CreditCard:
                          return (
                            <StripeCreditCardInfo
                              paymentMethod={paymentMethod}
                              withEdit
                            />
                          );
                        case StripePaymentMethodTypes.Pad:
                          return (
                            <StripePadInfo paymentMethod={paymentMethod} />
                          );
                        case StripePaymentMethodTypes.Bacs:
                          return (
                            <StripeBacsInfo paymentMethod={paymentMethod} />
                          );
                        case StripePaymentMethodTypes.Sepa:
                          return (
                            <StripeSepaInfo paymentMethod={paymentMethod} />
                          );
                        default:
                          return null;
                      }
                    })()}
                  </PaymentMethodTile>
                ))}
              </>
            )}
          </div>
          <div className="flex flex-col justify-between my-4 sm:mx-20 sm:my-0 sm:flex-row">
            {shouldShow3dsCreditCard ? (
              <TextButton
                onClick={() => setIsOpenAdd3dsCreditCardDrawer(true)}
                className="py-4 font-semibold"
                testId="pay_invoices-add_credit_card-btn"
              >
                {t("+ Add a new Card")}
              </TextButton>
            ) : (
              <TextButton
                onClick={() => setIsOpenAddPaymentMethodDrawer(true)}
                className="py-4 font-semibold"
                testId="pay_invoices-add_credit_card-btn"
              >
                {t("+ Add a new Card")}
              </TextButton>
            )}
            {shouldShowACH ? (
              <TextButton
                onClick={() => setIsOpenAddAchAccountDrawer(true)}
                className="py-4 font-semibold"
                testId="pay_invoices-add_ach_account-btn"
              >
                {t("+ Add a new ACH account")}
              </TextButton>
            ) : null}
            {shouldShowPAD ? (
              <TextButton
                onClick={() => setIsOpenAddPadAccountDrawer(true)}
                className="py-4 font-semibold"
                data-testid="pay_invoices-add_pad_account-btn"
              >
                {t("+ Add a new PAD account")}
              </TextButton>
            ) : null}
            {shouldShowBacs ? (
              <TextButton
                onClick={() => setIsOpenAddBacsAccountDrawer(true)}
                className="py-4 font-semibold"
                data-testid="pay_invoices-add_bacs_account-btn"
              >
                {t("+ Add a new BACS account")}
              </TextButton>
            ) : null}
            {shouldShowSepa ? (
              <TextButton
                onClick={() => setIsOpenAddSepaAccountDrawer(true)}
                className="py-4 font-semibold"
                data-testid="pay_invoices-add_sepa_account-btn"
              >
                {t("+ Add a new SEPA account")}
              </TextButton>
            ) : null}
          </div>
          <Add3dsCreditCardDrawer
            isOpen={isOpenAdd3dsCreditCardDrawer}
            onClose={() => setIsOpenAdd3dsCreditCardDrawer(false)}
            accountSignature={threeDSCreditCardSignature}
            account={account}
            isFirstPaymentMethod={
              !zuoraPaymentMethods.length && !stripePaymentMethods.length
            }
            redirectUrl={pathname}
            callbackData={{
              initialInvoiceIds: selectedInvoices
                .slice(0, MAX_INVOICES_TO_SEND_ADD_PAYMENT_CALLBACK)
                .map((i) => i.id),
              paymentMethodId: selectedPaymentMethodId,
            }}
          />
          <AddPaymentMethodDrawer
            isOpen={isOpenAddPaymentMethodDrawer}
            onClose={() => setIsOpenAddPaymentMethodDrawer(false)}
            accountSignature={creditCardSignature}
            account={account}
            accountLocation={location}
            isFirstPaymentMethod={
              !zuoraPaymentMethods.length && !stripePaymentMethods.length
            }
            redirectUrl={pathname}
            callbackData={{
              initialInvoiceIds: selectedInvoices
                .slice(0, MAX_INVOICES_TO_SEND_ADD_PAYMENT_CALLBACK)
                .map((i) => i.id),
              paymentMethodId: selectedPaymentMethodId,
            }}
          />
          <AddAchAccountDrawer
            isOpen={isOpenAddAchAccountDrawer}
            onClose={() => setIsOpenAddAchAccountDrawer(false)}
            accountSignature={achSignature}
            account={account}
            isFirstPaymentMethod={
              !zuoraPaymentMethods.length && !stripePaymentMethods.length
            }
            redirectUrl={pathname}
            callbackData={{
              initialInvoiceIds: selectedInvoices
                .slice(0, MAX_INVOICES_TO_SEND_ADD_PAYMENT_CALLBACK)
                .map((i) => i.id),
              paymentMethodId: selectedPaymentMethodId,
            }}
          />
          <AddPadAccountDrawer
            isOpen={isOpenAddPadAccountDrawer}
            onClose={() => setIsOpenAddPadAccountDrawer(false)}
            accountSignature={padSignature}
            account={account}
            isFirstPaymentMethod={
              !zuoraPaymentMethods.length && !stripePaymentMethods.length
            }
            redirectUrl={pathname}
            callbackData={{
              initialInvoiceIds: selectedInvoices
                .slice(0, MAX_INVOICES_TO_SEND_ADD_PAYMENT_CALLBACK)
                .map((i) => i.id),
              paymentMethodId: selectedPaymentMethodId,
            }}
          />
          <AddBacsAccountDrawer
            isOpen={isOpenAddBacsAccountDrawer}
            onClose={() => setIsOpenAddBacsAccountDrawer(false)}
            accountSignature={bacsSignature}
            account={account}
            isFirstPaymentMethod={
              !zuoraPaymentMethods.length && !stripePaymentMethods.length
            }
            redirectUrl={pathname}
            callbackData={{
              initialInvoiceIds: selectedInvoices
                .slice(0, MAX_INVOICES_TO_SEND_ADD_PAYMENT_CALLBACK)
                .map((i) => i.id),
              paymentMethodId: selectedPaymentMethodId,
            }}
          />
          <AddSepaAccountDrawer
            isOpen={isOpenAddSepaAccountDrawer}
            onClose={() => setIsOpenAddSepaAccountDrawer(false)}
            accountSignature={sepaSignature}
            account={account}
            isFirstPaymentMethod={
              !zuoraPaymentMethods.length && !stripePaymentMethods.length
            }
            redirectUrl={pathname}
            callbackData={{
              initialInvoiceIds: selectedInvoices
                .slice(0, MAX_INVOICES_TO_SEND_ADD_PAYMENT_CALLBACK)
                .map((i) => i.id),
              paymentMethodId: selectedPaymentMethodId,
            }}
          />
          {!isLoading ? (
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white lg:hidden shadow-card">
              <Button
                onClick={onSubmitPayment}
                className="w-full py-5"
                isDisabled={
                  !selectedInvoices.length || !selectedPaymentMethodId
                }
                data-testid="pay_invoices-button"
                isLoading={isPaying}
              >
                {t("Pay", {
                  balance: balanceToPay,
                })}
              </Button>
            </div>
          ) : null}
          <ConfirmationDialog
            contentClassName="max-w-[850px]! flex flex-row justify-between"
            cancelLabel={t("Go Back")}
            confirmLabel={t("Submit Payment")}
            descriptionClassName="flex flex-row"
            cancelButtonClassName="min-w-[165px]!"
            confirmButtonClassName="min-w-[165px]!"
            leftContentClassName="md:basis-2/3 flex flex-col justify-between"
            rightContentClassName="basis-1/3 border-solid border-2 border-grey-60 rounded-lg ml-4 grow min-w-72 hidden md:flex md:flex-col"
            title={
              <div className="font-bold">
                {t("Your payment includes a card processing fee")}
              </div>
            }
            description={
              <div>
                <div>
                  <Trans t={t}>
                    {t("creditCardFee.applies", {
                      creditCardFeeRate,
                      thresholdAmount: formattedThresholdAmount,
                    })}
                  </Trans>{" "}
                  {t(
                    "Please confirm your payment method and amount before submitting your payment."
                  )}
                </div>
                {/* Adding payment summary here for < md screen sizes */}
                <div className="flex flex-col m-5 mx-auto border-2 border-solid rounded-lg border-grey-60 grow min-w-64 max-w-96 md:hidden">
                  {PaymentSummary}
                </div>

                <div className="mt-4 grow">
                  {t("creditCardFee.avoid", {
                    bankTransferType: bankTransferType(location),
                  })}
                </div>
              </div>
            }
            // Adding Payment Summary on right panel for > md screen sizes
            rightContent={PaymentSummary}
            question={t("How would you like to proceed?")}
            isOpen={isCreditCardFeePaymentConfirmationOpen}
            onConfirm={submitPayment}
            onCancel={onCancelCreditCardFeeDialog}
            onClose={onCloseConfirmation}
            testId="pay_invoices_credit_card_fee_dialog"
          />
        </SplittedPage.Left>
        <SplittedPage.Right>
          <InvoiceSummary
            invoices={selectedInvoices}
            creditCardFeeToPay={creditCardFeeRequired ? creditCardFeeToPay : ""}
            totalPaymentToPay={totalPaymentToPay}
            currencyCode={account?.currencyCode}
            isLoading={isLoading}
            footer={
              <div>
                {creditCardFeeRequired && (
                  <div className="p-3 mt-6 border-2 border-solid rounded-lg bg-honey-99 border-honey-40">
                    {"* "}
                    <Trans t={t}>
                      {t("creditCardFee.applies", {
                        creditCardFeeRate,
                        thresholdAmount: formattedThresholdAmount,
                      })}{" "}
                    </Trans>
                    {t("creditCardFee.avoid", {
                      bankTransferType: bankTransferType(location),
                    })}
                    <div className="mt-4 grow">
                      {t(
                        "Fees are automatically processed shortly after a payment is submitted, and will be visible as a separate invoice in the billing portal."
                      )}
                    </div>
                  </div>
                )}
                <Button
                  onClick={onSubmitPayment}
                  className="w-full mt-9"
                  isDisabled={
                    !selectedInvoices.length || !selectedPaymentMethodId
                  }
                  data-testid="pay_invoices-button"
                  isLoading={isPaying}
                >
                  {t("Pay", {
                    balance: totalPaymentToPay,
                  })}
                </Button>
              </div>
            }
          />
        </SplittedPage.Right>
      </SplittedPage>
    </>
  );
}
